import React from 'react';
import clone from 'lodash/clone';
import union from 'lodash/union';

import Icon from 'relements/build/esm/ui/icon';
import Accordion from 'relements/build/esm/ui/accordion';
import ToastMessage from 'relements/build/esm/decorators/toastMessage';
import Text from 'relements/build/esm/ui/text';
import Tooltip from 'relements/build/esm/overlays/tooltip';
import Box from 'relements/build/esm/ui/box';

import FlaggedIcon from '@src/components/Icon/flagged.svg';
// Const
import {
  GOOGLE_ANALYTICS,
  PERMISSION_LEVELS,
  ENVIRONMENT,
  MIX_PANEL,
} from '@src/const';
import { DISAMBIGUATION_TOOLTIP } from '@src/constants/Tooltips';
import {
  AlertTypes,
  MAX_DISAMBIGUATION_MSG_LENGTH,
} from '@src/constants/MogamboConstants';
import BotBuilderContants from '@src/phrases';

// Components
import DisambiguationModal from '@src/components/DisambiguationModal';

// Stores
import CommonStore from '@src/stores/CommonStore';
import BotStore from '@src/store';

// Actions
import {
  openComponentModal,
  loadingComponentModal,
  closeComponentModal,
  getSynonyms,
  getMeanings,
  updateSynonyms,
  updateEntities,
  userHasPermission,
  permissionErrorAlert,
} from '@src/actions/BotActions';
import { sendGoogleAnalyticsEvents, CASName } from 'actions/_utils';

// Icons
import LanguagesIcon from './icons/Languages.svg';
import LanguagesDisabledIcon from './icons/LanguagesDisabled.svg';

import UserResponseInput from './UserResponseInput';
import UserResponse from './UserResponse';
import SynonymList from './SynonymList';
import SynonymSearch from './SynonymSearch';
import SynonymContextMenu from './SynonymContextMenu';

import styles from './UserResponseSynonymTable.scss';

const KEYWORDS = BotBuilderContants.getPhrase;

@ToastMessage()
export default class UserResponseSynonymTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      disambiguationMessageLength: '',
      isDisambiguationModalActive: false,
      isDisambiguationEnabled: true,
    };
  }

  componentDidMount() {
    const showOnlyDisambiguation = !!this.props.showOnlyDisambiguation;

    // Because of the old code structure, we had to add this option
    // to render disambiguation message seperately, hence
    // if the message is a disambiguation message is empty, we set its initial length to 0
    // it gets updated later by the editor itself.
    // This variable is used to display number of characters in disambiguation message
    const disambiguationMessageLength = showOnlyDisambiguation
      ? this.props.disambiguationValue.title.length
      : 0;

    this.setState({
      disambiguationMessageLength,
    });
  }

  render() {
    const showOnlyDisambiguation = !!this.props.showOnlyDisambiguation;
    const readOnlyClassName = this.props.readOnlyResponses ? 'read-only' : '';
    const userResponseClasses = !showOnlyDisambiguation
      ? `user-response-synonym-table ${readOnlyClassName}`
      : '';
    return (
      <div
        style={this.props.style}
        className={userResponseClasses}
        ref={(wrapper) => {
          this._wrapper = wrapper;
        }}
      >
        {showOnlyDisambiguation && !this.props.onFAQNode
          ? this.renderDisambiguation()
          : this.renderAllUserSays()}
        <SynonymContextMenu
          offset={this.props.contextMenuOffset}
          ref={(contextMenu) => {
            this._contextMenu = contextMenu;
          }}
        />
      </div>
    );
  }

  /**
   * renders all user says excluding disambiguation message
   */
  renderAllUserSays = () => {
    const readOnlyClassName = this.props.readOnlyResponses ? 'read-only' : '';
    return (
      <>
        {this.renderUserResponseInput()}
        {this.renderLegend(readOnlyClassName)}
        {this.renderSystemUserResponses()}
        {this.renderUserResponses()}
      </>
    );
  };

  /**
   * This method renders user responses which are added by user.
   */
  renderUserResponses = () => {
    // innerdata prop contains all the user response
    // screen prop contains the value for "source of response" selected, ex - independent, negative.
    // we simply check if innerdata is not null, (if it is null, we use empty object in place)
    // then we check if that screen key exists in innerdata(user responses) or not
    // ex - independent key exists in user response or not.
    // if it doesn't, assign an empty array to userResponses.
    const userResponses = (this.props.innerdata || {})[this.props.screen] || [];
    const editedData = this.props.editedData[this.props.screen] || [];
    // innerdata contains both user added and system-generated responses and both are
    // displayed in separate tables. Filtering the user responses added by the user.
    const normalUserResponse = userResponses.filter(
      (userResponse) => !userResponse.is_system_generated
    );

    return (
      <div className="user-responses">
        <div className="label">
          <Text variant="h4.bold">ADDED BY YOU AND OTHERS</Text>
        </div>
        {normalUserResponse.length > 0 ? (
          normalUserResponse.map((userResponse) => {
            if (
              userResponse.is_disambiguation_suggestion &&
              !this.props.onFAQNode
            )
              return null;

            return (
              <UserResponse
                key={userResponse.id}
                userResponse={userResponse}
                synonyms={this.props.synonyms}
                entities={this.props.entities}
                domainTitle={this.props.context.domainTitle}
                onAddTag={this._addTag}
                onRemoveTag={this._removeTag}
                onViewSynonyms={this._viewSynonyms}
                onSearchSynonyms={this._searchSynonyms}
                onToggleSynonyms={this._toggleSynonymsForWord}
                onToggleEntities={this._toggleEntitiesForWord}
                onEdit={this._editUserResponse}
                onToggle={this._toggleUserResponse}
                onFlag={this._flagUserResponse}
                isNewPipeline={this.props.context.isNewPipeline}
                readOnly={this.props.readOnlyResponses}
                focusContextMenu={this._focusContextMenu}
                blurContextMenu={this._blurContextMenu}
                getWrapperRect={this._getWrapperRect}
                updatedSynonyms={this.props.updatedSynonyms}
                updatedEntities={this.props.updatedEntities}
                editedData={editedData}
                typingSuggestionsDisabled={this.props.typingSuggestionsDisabled}
                onFAQNode={this.props.onFAQNode}
                onFlagDisambiguation={this._flagDisambiguationUserResponse}
                onConnection={!!this.props.updateDomainEntity}
              />
            );
          })
        ) : (
          <div className="empty-error">
            <Text variant="body.md">
              No {KEYWORDS('USER_MESSAGE.PLURAL.LOWERCASE')} added
            </Text>
          </div>
        )}
      </div>
    );
  };

  /**
   * This method will render user responses that are generated by the system through the augmentation process.
   * We'll filter the user responses generated by the system from innerdata.
   * System generated response will read only and will only have disable/enable option.
   */
  renderSystemUserResponses = () => {
    const systemUserResponses =
      (this.props.innerdata || {})[this.props.screen] || [];
    const editedData = this.props.editedData[this.props.screen] || [];
    const systemUserResponseFilter = systemUserResponses.filter(
      (userResponse) => userResponse.is_system_generated
    );
    // If no system-generated user responses available, dont show anything.
    if (systemUserResponseFilter.length < 1) return null;

    return (
      <div className="system-user-responses">
        <div className="label">
          <Text variant="h4.semi-bold">SMART VARIANTS</Text>
        </div>
        {systemUserResponseFilter.map((userResponse) => {
          return (
            <UserResponse
              key={userResponse.id}
              userResponse={userResponse}
              editedData={editedData}
              onToggle={this._toggleUserResponse}
              onFlag={this._flagUserResponse}
              readOnly
              typingSuggestionsDisabled
            />
          );
        })}
      </div>
    );
  };

  showDisambiguationModal = () => {
    const mixPanelTracker = BotStore.getMixPanel();
    const meta = CommonStore.getMetaTags();
    mixPanelTracker.track({
      event: MIX_PANEL.DISAMBIGUATION.SETTINGS_ICON_CLICKED,
      payload: {
        'Bot ID': meta.domain.id,
        'Bot Name': meta.domain.title,
        Business: meta.domain.client,
        'CAS Name': CASName(),
      },
    });
    this.setState({
      isDisambiguationModalActive: true,
    });
  };

  closeDisambiguationModal = () => {
    const mixPanelTracker = BotStore.getMixPanel();
    const meta = CommonStore.getMetaTags();
    mixPanelTracker.track({
      event: MIX_PANEL.DISAMBIGUATION.MODAL_CLOSED,
      payload: {
        'Bot ID': meta.domain.id,
        'Bot Name': meta.domain.title,
        Business: meta.domain.client,
        'CAS Name': CASName(),
      },
    });
    this.setState({
      isDisambiguationModalActive: false,
    });
  };

  /**
   * renders disambigaution message
   */
  renderDisambiguation = () => {
    const editedData = this.props.editedData[this.props.screen] || [];
    const isProdEnv = BotStore.getEnvironment() === ENVIRONMENT.PRODUCTION;
    const {
      disambiguationMessageLength = 0,
      isDisambiguationModalActive,
    } = this.state;
    /**
     * Disable Disambiguation Configuration if disambiguationValue is not there or
     * If the User Says is of Connections then check for edited prop otherwise check for editedData.
     * As Connection's editedData is not an empty object in different scenarios that's why using a flag
     */
    const isDisambiguationConfigDisable =
      !this.props.disambiguationValue.title.length ||
      (this.props.isConnectionUserSays
        ? this.props.edited
        : editedData.length !== 0);
    const supportedLanguages = CommonStore.getSupportedLanguages();

    return (
      <div className={styles.disambiguation}>
        <div className={styles.disambiguationHeader}>
          <div className={styles.disambiguationHeading}>
            <div className={styles.disambiguationHeadingLabel}>
              <Text variant="h4.semi-bold">
                &quot;Did you mean&quot; button text
              </Text>
            </div>
            <Icon
              src="info"
              className={styles.disambiguationHeadingIcon}
              prefixClassName={styles.disambiguationHeadingTooltip}
              tooltip={this.renderDisambigautionTooltip()}
            />
          </div>
          {/**
           * Hide in these scenarios:
           * If bot has only one language enabled, i.e., English
           * Or if bot is not there in feature toggle enable_disambiguation
           * Or if the env is Prod
           */
          supportedLanguages.length > 1 &&
            this.state.isDisambiguationEnabled &&
            !isProdEnv && (
              <>
                <div className={styles.disambiguationHeaderLine} />
                <Tooltip
                  themed
                  size="large"
                  position="TOP"
                  tooltip={this.renderDisableTooltip(
                    isDisambiguationConfigDisable
                  )}
                >
                  <Icon
                    src={
                      !isDisambiguationConfigDisable
                        ? LanguagesIcon
                        : LanguagesDisabledIcon
                    }
                    onClick={
                      !isDisambiguationConfigDisable
                        ? this.showDisambiguationModal
                        : null
                    }
                  />
                </Tooltip>
              </>
            )}
        </div>
        <div className={styles.disambiguationInput}>
          <UserResponse
            key={this.props.disambiguationValue.id}
            userResponse={this.props.disambiguationValue}
            synonyms={this.props.synonyms}
            entities={this.props.entities}
            domainTitle={this.props.context.domainTitle}
            onAddTag={this._addTag}
            onRemoveTag={this._removeTag}
            onViewSynonyms={this._viewSynonyms}
            onSearchSynonyms={this._searchSynonyms}
            onToggleSynonyms={this._toggleSynonymsForWord}
            onToggleEntities={this._toggleEntitiesForWord}
            isNewPipeline={this.props.context.isNewPipeline}
            onEdit={this._onDisambiguationChange}
            onToggle={this._toggleUserResponse}
            onFlag={this._flagUserResponse}
            readOnly={this.props.readOnlyResponses}
            focusContextMenu={this._focusContextMenu}
            blurContextMenu={this._blurContextMenu}
            getWrapperRect={this._getWrapperRect}
            updatedSynonyms={this.props.updatedSynonyms}
            updatedEntities={this.props.updatedEntities}
            editedData={editedData}
            typingSuggestionsDisabled={this.props.typingSuggestionsDisabled}
            updateCharLength={this.updateMessageLength}
            onConnection={!!this.props.updateDomainEntity}
            showOnlyDisambiguation
          />
          <div className={styles.disambiguationCount}>
            {`${
              disambiguationMessageLength.length === 1
                ? `0${disambiguationMessageLength}`
                : `${disambiguationMessageLength}`
            }/${MAX_DISAMBIGUATION_MSG_LENGTH}`}
          </div>
        </div>
        <span className={styles.disambiguationHelp}>
          This message will also be treated as a{' '}
          {KEYWORDS('USER_MESSAGE.LOWERCASE')} expression
        </span>
        {isDisambiguationModalActive && (
          <DisambiguationModal
            isActive={isDisambiguationModalActive}
            onClose={this.closeDisambiguationModal}
            sentenceId={this.props.disambiguationValue.id}
          />
        )}
      </div>
    );
  };

  /**
   * renders the tooltip of disabled disambiguation cog icon
   * @param {boolean} isDisambiguationConfigDisable
   */
  renderDisableTooltip = (isDisambiguationConfigDisable) => {
    return (
      <Box padding="8px 4px">
        {!isDisambiguationConfigDisable ? (
          'Configure the button text in other languages.'
        ) : (
          <>
            Configuration for button text in other
            <br />
            languages will be enabled once the
            <br />
            button text is saved.
          </>
        )}
      </Box>
    );
  };

  /**
   * renders disambiguation tooltip
   */
  renderDisambigautionTooltip = () => {
    return (
      <div style={{ maxWidth: 150 }}>
        <span>{DISAMBIGUATION_TOOLTIP.text}</span>
        <a
          className={styles.addLabelTooltipIconLink}
          href={DISAMBIGUATION_TOOLTIP.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          Learn More
        </a>
      </div>
    );
  };

  /**
   * updates the number of character in disambiguation message
   * @param {(string|number)} newLength number of character in disambiguation message
   */
  updateMessageLength = (newLength) => {
    this.setState({ disambiguationMessageLength: newLength });
  };

  renderLegend = (readOnlyClassName) => (
    <Accordion className={`legend-accordion ${readOnlyClassName}`}>
      <Accordion.Item
        itemIndex={0}
        active={this.state.showLegend}
        renderHeader={() => (
          <div className="legend-accordion-header">
            <Icon src="angle-down" className="legend-accordion-header-icon" />
            Legend
          </div>
        )}
        onChange={() => this.setState({ showLegend: !this.state.showLegend })}
        className="legend-accordion-item"
      >
        <div className="legend">
          <div className="legend-item">
            <Icon src={FlaggedIcon} className="legend-item-icon" />
            <span>
              {!this.props.typingSuggestionsDisabled
                ? `Flagged ${KEYWORDS(
                    'USER_MESSAGE.PLURAL.CAPITALIZE'
                  )} will be visible to the user as a typing suggestion. `
                : 'Typing suggestion is not applicable for non-start Step. '}
              <a>Learn More</a>
            </span>
          </div>
          <div className="legend-examples">
            <div className="legend-item-row">
              <div className="legend-item">
                <Icon
                  src={() => <span className="underline blue dashed" />}
                  className="legend-item-icon"
                />
                <span>Synonym Disabled</span>
              </div>
              <div className="legend-item">
                <Icon
                  src={() => <span className="underline blue" />}
                  className="legend-item-icon"
                />
                <span>Synonym Enabled</span>
              </div>
            </div>
            <div className="legend-item-row">
              <div className="legend-item">
                <Icon
                  src={() => <span className="underline green dashed" />}
                  className="legend-item-icon"
                />
                <span>Entity Disabled</span>
              </div>
              <div className="legend-item">
                <Icon
                  src={() => <span className="underline green" />}
                  className="legend-item-icon"
                />
                <span>Entity Enabled</span>
              </div>
            </div>
            <div className="legend-item-row">
              <div className="legend-item">
                <Icon src="visible" className="legend-item-icon" />
                <span>Enable {KEYWORDS('USER_MESSAGE.PLURAL.CAPITALIZE')}</span>
              </div>
              <div className="legend-item">
                <Icon src="invisible" className="legend-item-icon" />
                <span>
                  Disabled {KEYWORDS('USER_MESSAGE.PLURAL.CAPITALIZE')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Accordion.Item>
    </Accordion>
  );

  renderUserResponseInput = () =>
    !this.props.readOnlyResponses ? (
      <header className="user-response-synonym-table-header">
        <div className="add-response-label">
          <Text variant="h4.semi-bold">{this.props.screenTitle}</Text>
        </div>
        <div className="add-response-wrapper">
          <UserResponseInput
            active
            value={this.state.inputText}
            synonyms={this.props.synonyms}
            entities={this.props.entities}
            domainTitle={this.props.context.domainTitle}
            onChange={this._handleInputTextChange}
            onAdd={this._addUserResponse}
            editable
            noAutoFocus={this.props.noAutoFocus}
            onReturn={(text) =>
              this._handleKeyUpFromNewResponse(text, this._addUserResponse) &&
              'handled'
            }
            getWrapperRect={this._getWrapperRect}
          />
          <div className="add-response-cta">
            <Text variant="h4.semi-bold">⏎ Press Enter</Text>
          </div>
        </div>
      </header>
    ) : null;

  _handleInputTextChange = (e) => {
    const inputText = e.target.value;
    this.setState({ inputText });
  };

  _handleKeyUpFromNewResponse = (text, cb) => {
    if (text.length > 0) cb(text);
    return 'handled';
  };

  /**
   * updates disambiguation message in store
   * @param {Object} selectedUserResponse user response edited
   * @param {string} newTitle new title of the user response
   */
  _onDisambiguationChange = (selectedUserResponse, newTitle) => {
    const { connectingNode } = this.props.context;
    const updatedUserResponse = {
      ...selectedUserResponse,
      title: newTitle.trim(),
    };
    let allUserResponses = {
      ...this.props.innerdata,
    };
    let editedData = {
      ...this.props.editedData,
    };

    // checks if user responses already exist or not
    if (
      typeof this.props.innerdata === 'object' &&
      Object.keys(this.props.innerdata).length
    ) {
      let newResponses = [];

      // if connection node as a key exists in user responses
      // filter out the disambiguation response
      // and add the new response to the filtered array
      if (this.props.innerdata[connectingNode]) {
        const filteredResponses = this.props.innerdata[connectingNode].filter(
          (response) => !response.is_disambiguation_suggestion
        );
        filteredResponses.unshift(updatedUserResponse);
        newResponses = filteredResponses;
      } else {
        newResponses = [updatedUserResponse];
      }

      allUserResponses = {
        ...allUserResponses,
        [connectingNode]: newResponses,
      };
    } else if (!Object.keys(this.props.innerdata).length) {
      allUserResponses = {
        [connectingNode]: [updatedUserResponse],
      };
    }

    // if edited data exists, filter out the disambiguation response
    // and add the new user response back to array
    if (Object.keys(editedData).length && editedData[connectingNode]) {
      const filteredResponses = editedData[connectingNode].filter(
        (response) => !response.is_disambiguation_suggestion
      );
      filteredResponses.unshift(updatedUserResponse);

      editedData = {
        ...editedData,
        [connectingNode]: filteredResponses,
      };
    } else {
      editedData = {
        ...editedData,
        [connectingNode]: [updatedUserResponse],
      };
    }

    this.props.updateInputs(allUserResponses, editedData);

    // if disambiguation message is newly added
    // get its synonym
    if (selectedUserResponse.new) {
      getSynonyms(newTitle);
    }
  };

  _focusContextMenu = (
    contextMenuOptions,
    contextMenuPosition,
    wrapperRect
  ) => {
    if (!contextMenuOptions) return;
    this._contextMenu.focus(
      contextMenuOptions,
      contextMenuPosition,
      wrapperRect
    );
  };

  _blurContextMenu = (e) => {
    if (this._contextMenu) this._contextMenu.blur();
  };

  _getWrapperRect = () => {
    return this._wrapper.getBoundingClientRect();
  };

  _viewSynonyms = (synonym, userResponse) => {
    const clusterName = synonym.cluster_name.split('__')[1];
    const listingTitle = `${clusterName}: Synonym List`;
    const listingSubtitle = 'Enable and add synonyms';
    const variants = union(synonym.variants, synonym.lemma_variants);
    const ListingComponent = (
      <SynonymList
        title={clusterName}
        definition={synonym.definition}
        variants={variants}
        enabled={synonym.active}
        context={this.props.context}
        onToggle={(enabled) => this._toggleSynonymsForWord(synonym, enabled)}
        onSearch={(e) => this._searchSynonyms(synonym, userResponse, false)}
        onVariantAdd={(newVariant) => this._addVariant(synonym, newVariant)}
        onVariantDelete={(variantIndex) =>
          this._deleteVariant(synonym, variantIndex)
        }
      />
    );
    openComponentModal(
      ListingComponent,
      listingTitle,
      listingSubtitle,
      'CROSS',
      false,
      this._closeListing,
      undefined
    );
  };

  _searchSynonyms = (cluster, userResponse, closeable) => {
    const word = cluster.source_word;
    const searchTitle = `${word}: More Meanings`;
    const searchSubtitle = 'Add a meaning to the list';
    const modalIcon = closeable ? 'CROSS' : 'BACK';
    loadingComponentModal(searchTitle, searchSubtitle, 'BACK', false);
    getMeanings(word, () => {
      // Dispatch on the next tick so that we don't dispatch in the middle of another dispatch
      setTimeout(() => {
        const SearchComponent = (
          <SynonymSearch
            word={word}
            meanings={this.props.meanings}
            onAdd={(meaning) =>
              this._addMeaning(meaning, cluster, userResponse)
            }
          />
        );
        openComponentModal(
          SearchComponent,
          searchTitle,
          searchSubtitle,
          modalIcon,
          false,
          this._closeListing
        );
      }, 200);
    });
  };

  _addMeaning = (meaning, cluster, userResponse) => {
    const updatedCluster = {
      cluster_name: cluster.cluster_name,
      variants: meaning.synonyms,
      lemma_variants: meaning.synonyms,
      domain_list: cluster.domain_list || [],
      domain: { name: this.props.context.domainTitle, active: true },
      definition: meaning.definition,
      source_word: cluster.source_word,
    };

    updatedCluster.domain_list.push(this.props.context.domainTitle);

    const source_words = cluster.source_word.toString().split(',');
    const { variants } = updatedCluster;
    source_words.map((sourceWord) => {
      if (variants.indexOf(sourceWord) < 0) {
        updatedCluster.variants.push(sourceWord);
      }
    });

    this._updateSynonyms(updatedCluster);
    this._viewSynonyms(updatedCluster, userResponse);
  };

  _addTag = (selectedText, selectedUserResponse) => {
    this._updateUserResponses(selectedUserResponse, (foundUserResponse) => {
      foundUserResponse.tags = clone(foundUserResponse.tags);
      foundUserResponse.tags.push(selectedText);
      foundUserResponse.color = true;
      return foundUserResponse;
    });
    sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.USER_SAYS,
      GOOGLE_ANALYTICS.EVENTS.TAG_EDIT,
      GOOGLE_ANALYTICS.LABEL.TAG_ADD,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );
  };

  _removeTag = (selectedText, selectedUserResponse) => {
    this._updateUserResponses(selectedUserResponse, (foundUserResponse) => {
      foundUserResponse.tags = foundUserResponse.tags.filter(
        (tag) => tag !== selectedText
      );
      foundUserResponse.color = true;
      return foundUserResponse;
    });
    sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.USER_SAYS,
      GOOGLE_ANALYTICS.EVENTS.TAG_EDIT,
      GOOGLE_ANALYTICS.LABEL.TAG_REMOVE,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );
  };

  _addUserResponse = (text) => {
    if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
      return permissionErrorAlert();
    }
    const { connectingNode, domainId, nodeId } = this.props.context;

    const userResponses = clone(this.props.innerdata);
    const editedData = clone(this.props.editedData);

    editedData[connectingNode] = editedData[connectingNode] || [];
    userResponses[connectingNode] = userResponses[connectingNode] || [];

    const newUserResponse = {
      id: Math.floor(Math.random() * 10000).toString(),
      connecting_node: connectingNode,
      domain_id: domainId,
      node_id: nodeId,
      title: text,
      active: true,
      tags: [],
      color: true,
    };
    userResponses[connectingNode] = [newUserResponse].concat(
      userResponses[connectingNode]
    );
    editedData[connectingNode] = [newUserResponse].concat(
      editedData[connectingNode]
    );

    this.props.updateInputs(userResponses, editedData);
    getSynonyms(text);
  };

  _editUserResponse = (selectedUserResponse, newTitle) => {
    this._updateUserResponses(selectedUserResponse, (foundUserResponse) => {
      foundUserResponse = { ...foundUserResponse, title: newTitle };
      return foundUserResponse;
    });
  };

  _toggleUserResponse = (selectedUserResponse) => {
    this._updateUserResponses(selectedUserResponse, (foundUserResponse) => {
      const active = !foundUserResponse.active;
      foundUserResponse = { ...foundUserResponse, active };
      return foundUserResponse;
    });
  };

  _flagUserResponse = (selectedUserResponse) => {
    if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
      return permissionErrorAlert();
    }
    this._updateUserResponses(selectedUserResponse, (foundUserResponse) => {
      const is_typing_suggestion = !foundUserResponse.is_typing_suggestion;
      foundUserResponse = { ...foundUserResponse, is_typing_suggestion };
      return foundUserResponse;
    });
  };

  _flagDisambiguationUserResponse = (selectedUserResponse) => {
    // user should have edit permissions
    if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
      return permissionErrorAlert();
    }

    // disambiguation message should not be greater than 21 characters
    if (selectedUserResponse.title.length > 21) {
      this.props.activateToastMessage({
        title:
          'Disambiguation Message Length Should be not more than 21 characters.',
        type: AlertTypes.WARNING,
        timeout: 2000,
      });
      return;
    }

    const { connectingNode } = this.props.context;
    const userResponses = clone(this.props.innerdata);
    const editedData = clone(this.props.editedData);

    // check if edited data for connecting node present, if present use that data
    editedData[connectingNode] = editedData[connectingNode]
      ? editedData[connectingNode]
      : [];

    // map user responses to flag/unflag responses
    userResponses[connectingNode] = userResponses[connectingNode].map(
      (userResponse, i) => {
        /* if response is diff than what is selected, 	
        check if disambiguation is true, if yes, set to false, add to edited data	
        */

        if (userResponse.id !== selectedUserResponse.id) {
          if (userResponse.is_disambiguation_suggestion) {
            userResponse.is_disambiguation_suggestion = false;
            editedData[connectingNode][i] = userResponse;
          }
        }
        // for the selected response, we want to set disambiguation flag to true
        else {
          const is_disambiguation_suggestion = !userResponse.is_disambiguation_suggestion;
          userResponse = { ...userResponse, is_disambiguation_suggestion };
          editedData[connectingNode][i] = userResponse;
        }
        return userResponse;
      }
    );

    this.props.updateInputs(userResponses, editedData);
  };

  _updateUserResponses = (selectedUserResponse, fn) => {
    if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
      return permissionErrorAlert();
    }
    const { connectingNode } = this.props.context;

    const userResponses = clone(this.props.innerdata);
    const editedData = clone(this.props.editedData);

    editedData[connectingNode] = editedData[connectingNode]
      ? editedData[connectingNode]
      : [];

    userResponses[connectingNode] = userResponses[connectingNode].map(
      (userResponse, i) => {
        if (userResponse.id === selectedUserResponse.id) {
          userResponse = fn(userResponse);
          userResponse.tags = userResponse.tags.filter(
            (tag) => userResponse.title.indexOf(tag) > -1
          );
          editedData[connectingNode][i] = userResponse;
        }
        return userResponse;
      }
    );

    this.props.updateInputs(userResponses, editedData);
  };

  _addVariant = (cluster, newVariant) => {
    const updatedCluster = cluster;
    const { domainTitle } = this.props.context;
    const { variants } = updatedCluster;
    const source_words = cluster.source_word.toString().split(',');

    if (variants.indexOf(newVariant) === -1) {
      updatedCluster.variants = [newVariant].concat(updatedCluster.variants);
    }
    source_words.map((sourceWord) => {
      if (variants.indexOf(sourceWord) < 0) {
        updatedCluster.variants = updatedCluster.variants.concat([sourceWord]);
      }
    });
    updatedCluster.domain = {
      name: domainTitle,
      active: cluster.domain_list.indexOf(domainTitle) > -1,
    };
    this._updateSynonyms(updatedCluster);
  };

  _deleteVariant = (cluster, variant) => {
    const updatedCluster = cluster;
    const { variants } = updatedCluster;
    const { domainTitle } = this.props.context;
    const source_words = cluster.source_word.toString().split(',');

    updatedCluster.variants = updatedCluster.variants.filter(
      (x) => x !== variant
    );
    updatedCluster.lemma_variants = updatedCluster.lemma_variants.filter(
      (x) => x !== variant
    );

    source_words.map((sourceWord) => {
      if (variants.indexOf(sourceWord) < 0) {
        updatedCluster.variants.push(sourceWord);
      }
    });
    updatedCluster.domain = {
      name: domainTitle,
      active: cluster.domain_list.indexOf(domainTitle) > -1,
    };
    this._updateSynonyms(updatedCluster);
  };

  _toggleSynonymsForWord = (cluster, enabled) => {
    const updatedCluster = { ...cluster };
    updatedCluster.active = enabled;

    this._updateSynonyms(updatedCluster);
  };

  _updateSynonyms = (cluster) => {
    const { updateSynonymsCluster = () => {} } = this.props;
    if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
      return permissionErrorAlert();
    }
    // Called to enable the save callback method on adding synonyms to the domain
    this.props.updateInputs(this.props.innerdata, this.props.editedData);

    updateSynonymsCluster(cluster);
    updateSynonyms(cluster);
  };

  _toggleEntitiesForWord = (entity, enabled) => {
    const updatedEntity = { ...entity };
    updatedEntity.active = enabled;

    this._updateEntities(updatedEntity);
  };

  _updateEntities = (entity) => {
    const { updateDomainEntity = () => {} } = this.props;
    if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
      return permissionErrorAlert();
    }
    // Called to enable the save callback method on adding synonyms to the domain
    this.props.updateInputs(this.props.innerdata, this.props.editedData);

    updateDomainEntity(entity);
    updateEntities(entity);
  };

  _closeListing = () => {
    closeComponentModal();
  };
}
